import { message } from "antd";

// Utilitários para tratamento de erros consistente em toda a aplicação

// Detecta se uma mensagem já está em português (acentos ou palavras típicas).
export const isLikelyPortuguese = (msg: string): boolean =>
  /[ãõçáàâéêíóôú]|(\b(não|uma|você|para|com|seu|sua|conta|erro|este|esta|mais|pelo|pela|tente|novamente|verifique|ocorreu)\b)/i.test(
    msg,
  );

/**
 * Traduz mensagens de erro conhecidas da API (inglês) para português.
 * Retorna null quando não há tradução mapeada.
 */
export const translateKnownApiError = (message: string): string | null => {
  const m = message.toLowerCase().trim();

  // Autenticação
  if (
    m.includes("invalid email or password") ||
    m.includes("invalid credentials")
  )
    return "E-mail ou senha incorretos. Verifique suas credenciais e tente novamente.";
  if (m.includes("user not found"))
    return "Usuário não encontrado. Verifique se o e-mail está correto.";
  if (
    m.includes("user already exists") ||
    m.includes("email already in use") ||
    m.includes("email already exists")
  )
    return "Este e-mail já está em uso. Tente fazer login ou recuperar sua senha.";
  if (
    m.includes("account domain already exists") ||
    m.includes("account with the given domain already exists") ||
    m.includes("domain already exists") ||
    m.includes("domain already in use")
  )
    return "Este domínio já está em uso. Escolha outro domínio.";
  if (m.includes("account not found")) return "Conta não encontrada.";
  if (m.includes("account already exists")) return "Esta conta já existe.";
  if (m.includes("session expired") || m.includes("token expired"))
    return "Sessão expirada. Faça login novamente.";
  if (m.includes("unauthorized") || m.includes("not authorized"))
    return "Você não tem autorização para realizar esta ação.";

  // Permissões
  if (
    m.includes("insufficient permissions") ||
    m.includes("permission denied") ||
    m.includes("access denied")
  )
    return "Você não tem permissão para realizar esta ação.";
  if (m.includes("forbidden")) return "Acesso negado.";

  // Vitrine (POST/PATCH /showcase)
  if (m.includes("slug") && m.includes("is unavailable"))
    return "Este endereço já está em uso. Escolha outro.";
  if (m.includes("showcase for account") && m.includes("already exists"))
    return "Esta conta já tem uma vitrine.";

  // Senha
  if (m.includes("password too weak") || m.includes("weak password"))
    return "A senha deve ter pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas e números.";
  if (
    m.includes("password does not match") ||
    m.includes("passwords do not match")
  )
    return "As senhas não coincidem.";
  if (m.includes("invalid password")) return "Senha inválida.";

  // Validação
  if (m.includes("invalid email") || m.includes("email format"))
    return "E-mail inválido. Verifique o formato e tente novamente.";
  if (m.includes("required field") || m.includes("field is required"))
    return "Campo obrigatório não preenchido.";
  if (m.includes("invalid format") || m.includes("format error"))
    return "Formato inválido. Verifique os dados inseridos.";
  if (m.includes("invalid data") || m.includes("validation error"))
    return "Dados inválidos. Verifique as informações e tente novamente.";

  // Limites e quotas
  if (m.includes("rate limit") || m.includes("too many requests"))
    return "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
  if (m.includes("limit exceeded") || m.includes("quota exceeded"))
    return "Limite excedido. Considere fazer um upgrade do plano.";

  // Integração
  if (m.includes("integration failed") || m.includes("connection failed"))
    return "Falha na integração. Verifique suas credenciais e tente novamente.";
  if (m.includes("api key invalid")) return "Chave de API inválida.";

  // Servidor
  if (m.includes("service unavailable") || m.includes("maintenance"))
    return "Serviço temporariamente indisponível. Tente novamente em alguns minutos.";
  if (m.includes("internal server error") || m.includes("unexpected error"))
    return "Erro interno do servidor. Tente novamente em alguns instantes.";
  if (m.includes("network error") || m.includes("request failed"))
    return "Erro de conexão. Verifique sua internet e tente novamente.";

  return null;
};

/**
 * Traduz mensagens de erro da API (inglês) para português.
 * Retorna a mensagem original caso não haja tradução mapeada.
 */
export const translateApiError = (message: string): string =>
  translateKnownApiError(message) ?? message.charAt(0).toUpperCase() + message.slice(1);

const translateFieldName = (name: string): string => {
  const map: Record<string, string> = {
    email: "E-mail",
    password: "Senha",
    name: "Nome",
    "account.name": "Nome da conta",
    accountName: "Nome da conta",
    domain: "Domínio",
    documentNumber: "CPF/CNPJ",
    contactEmail: "E-mail de contato",
    contactPhone: "Telefone de contato",
    contactName: "Nome de contato",
    planId: "Plano",
    role: "Função",
  };
  return map[name] ?? name;
};

const translateFieldMessage = (message: string): string => {
  const m = message.toLowerCase().trim();
  if (m === "already exists" || m.includes("already exists"))
    return "já existe";
  if (m === "not found" || m.includes("not found")) return "não encontrado";
  if (m === "required" || m.includes("is required") || m.includes("required"))
    return "é obrigatório";
  if (m.includes("invalid") || m.includes("invalid format"))
    return "é inválido";
  if (m.includes("too short") || m.includes("min length"))
    return "é muito curto";
  if (m.includes("too long") || m.includes("max length"))
    return "é muito longo";
  if (m.includes("too weak")) return "é muito fraca";
  if (m.includes("does not match") || m.includes("mismatch"))
    return "não confere";
  return message;
};

/**
 * Extrai e traduz a mensagem de erro de uma resposta da API.
 * Quando o erro contém `fields`, monta mensagem por campo.
 * Retorna o fallback caso não haja mensagem no erro.
 */
export const getApiErrorMessage = (
  error: any,
  fallback = "Ops! Algo deu errado. Tente novamente.",
): string => {
  const data = error?.response?.data;
  if (!data) return fallback;

  const fields: Array<{ name: string; message: string }> | undefined =
    data.fields;

  if (fields && fields.length > 0) {
    const parts = fields.map(
      (f) =>
        `${translateFieldName(f.name)} ${translateFieldMessage(f.message)}`,
    );
    const result = parts.join(". ") + ".";
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  const raw: string | undefined = data.message;
  if (!raw) return fallback;
  // Mensagem em inglês sem tradução não chega crua ao usuário: usa o fallback.
  return translateKnownApiError(raw) ?? (isLikelyPortuguese(raw) ? raw : fallback);
};

/**
 * Verifica se o interceptor já mostrou uma mensagem de erro para evitar duplicação
 * @param error - O erro capturado no catch
 * @returns true se o interceptor já mostrou uma mensagem, false caso contrário
 */
export const isErrorMessageAlreadyShown = (error: any): boolean => {
  // O interceptor pode marcar tanto `error._errorMessageShown` quanto
  // `error.config._errorMessageShown`. Checar ambas para compatibilidade.
  if (!error) return false;
  const direct = !!(error as any)?._errorMessageShown;
  const configFlag = !!(
    error.config && (error.config as any)?._errorMessageShown
  );
  return direct || configFlag;
};

/**
 * Wrapper para mostrar mensagem de erro apenas se o interceptor não mostrou
 * @param error - O erro capturado no catch
 * @param fallbackMessage - Mensagem padrão a ser mostrada se o interceptor não mostrou nada
 * @param messageApi - Instância do message API do Ant Design
 */
export const showErrorMessageSafely = (
  error: any,
  fallbackMessage: string,
  messageApi: any,
): void => {
  if (!isErrorMessageAlreadyShown(error)) {
    messageApi.error(fallbackMessage);
  }
};

/**
 * Mostra UM toast de erro para a ação: se o interceptor já mostrou a mensagem
 * do backend, não repete; senão mostra a mensagem traduzida (ou o fallback).
 */
export const showApiError = (error: any, fallback: string): void => {
  if (isErrorMessageAlreadyShown(error)) return;
  message.error(getApiErrorMessage(error, fallback));
};
